import React, { useState, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import IconButton from "@material-ui/core/IconButton";
import SaveIcon from "@material-ui/icons/Save";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import GetAppIcon from "@material-ui/icons/GetApp"; // Icono Excel
import PersonIcon from "@material-ui/icons/Person"; // Icono Persona
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import GroupIcon from "@material-ui/icons/Group";
import AssessmentIcon from "@material-ui/icons/Assessment";
import FiberNewIcon from "@material-ui/icons/FiberNew";
import Chip from "@material-ui/core/Chip";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import GuideOpenButton from "../../components/GuideOpenButton";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: "12px",
    boxShadow: theme.mode === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
  },

  // ✅ CORREGIDO: HeaderWrapper dinámico según tema
  headerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.mode === "dark" ? "#374151" : "#f5f5f5",
    borderRadius: 12,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    boxShadow: theme.mode === "dark" ? "0 2px 4px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
  },

  // ✅ CORREGIDO: SelectControl dinámico según tema
  selectControl: {
    minWidth: 300,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.mode === "dark" ? "#374151" : "white",
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#fafafa",
      },
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "#4B5563" : "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#f5f5f5",
      },
    },
  },

  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    fontSize: "0.875rem",
    padding: theme.spacing(1.5),
    position: "sticky",
    top: 0,
    zIndex: 1,
  },

  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : "#f5f5f5",
    },
    "&:nth-of-type(even)": {
      backgroundColor: theme.mode === "dark" ? "#1f2937" : "#fafafa",
    },
    "& td": {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    },
  },

  // ✅ CORREGIDO: TableCell dinámico según tema
  tableCell: {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    color: theme.palette.text.primary,
  },

  // ✅ ESTILOS DE BOTONES MEJORADOS
  buttonPrimary: {
    backgroundColor: "#1976d2",
    color: "white",
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: theme.spacing(1, 3),
    borderRadius: 8,
    boxShadow: "0 2px 4px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      backgroundColor: "#1565c0",
      boxShadow: "0 4px 8px rgba(25, 118, 210, 0.4)",
    },
    "&:disabled": {
      backgroundColor: "#cccccc",
      boxShadow: "none",
    },
  },

  buttonSuccess: {
    backgroundColor: "#2e7d32",
    color: "white",
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: theme.spacing(1, 3),
    borderRadius: 8,
    boxShadow: "0 2px 4px rgba(46, 125, 50, 0.3)",
    "&:hover": {
      backgroundColor: "#1b5e20",
      boxShadow: "0 4px 8px rgba(46, 125, 50, 0.4)",
    },
    "&:disabled": {
      backgroundColor: "#cccccc",
      boxShadow: "none",
    },
  },

  actionButton: {
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.1)",
    },
  },

  // ✅ ESTILOS DE CHIPS MEJORADOS
  statusChip: {
    fontWeight: "bold",
    fontSize: "0.75rem",
    padding: theme.spacing(0.5, 1),
  },

  adminChip: {
    backgroundColor: "#388e3c",
    color: "white",
    boxShadow: "0 2px 4px rgba(56, 142, 60, 0.3)",
  },

  ownerChip: {
    backgroundColor: "#d32f2f",
    color: "white",
    boxShadow: "0 2px 4px rgba(211, 47, 47, 0.3)",
    fontWeight: "bold",
  },

  superadminChip: {
    backgroundColor: "#7b1fa2",
    color: "white",
    boxShadow: "0 2px 4px rgba(123, 31, 162, 0.3)",
    fontWeight: "bold",
  },

  userChip: {
    backgroundColor: "#616161",
    color: "white",
    boxShadow: "0 2px 4px rgba(97, 97, 97, 0.3)",
  },

  newChip: {
    backgroundColor: "#ff9800",
    color: "white",
    fontWeight: "bold",
    boxShadow: "0 2px 4px rgba(255, 152, 0, 0.3)",
  },

  membersChip: {
    backgroundColor: "#2196f3",
    color: "white",
    fontWeight: "bold",
  },

  groupTypeChip: {
    backgroundColor: "#1976d2",
    color: "white",
    fontWeight: "bold",
    boxShadow: "0 2px 4px rgba(25, 118, 210, 0.3)",
  },

  communityTypeChip: {
    backgroundColor: "#9c27b0",
    color: "white",
    fontWeight: "bold",
    boxShadow: "0 2px 4px rgba(156, 39, 176, 0.3)",
  },

  // ✅ ESTILOS DE ALERTAS
  infoBox: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
  },

  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },

  emptyStateIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
    opacity: 0.3,
  },

  // ✅ CORREGIDO: StatsCard dinámico según tema
  statsCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    boxShadow: theme.mode === "dark" ? "0 2px 4px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.05)",
    color: theme.palette.text.primary,
  },
}));

const GroupsWA = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const socketManager = useContext(SocketContext);

  // Estados
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useViewMode("groupsWA");
  const [saving, setSaving] = useState(false);
  const [groupsFromWhatsApp, setGroupsFromWhatsApp] = useState([]);
  const [groupsFromDB, setGroupsFromDB] = useState([]);
  const [newGroups, setNewGroups] = useState([]);
  const [savedGroups, setSavedGroups] = useState(new Set());

  // ✅ Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // ✅ Estados para extracción y exportación de participantes
  const [extractingGroups, setExtractingGroups] = useState(new Set());
  const [exportingGroups, setExportingGroups] = useState(new Set());
  const extractionProgressRef = useRef({});

  // Conexiones disponibles (WhatsApp y Telegram)
  const connectedWhatsApps = whatsApps.filter((w) => w.status === "CONNECTED");
  const selectedConnection = connectedWhatsApps.find((w) => w.id === Number(selectedWhatsapp));
  const isTelegramConnection = String(selectedConnection?.type || "").toLowerCase() === "telegram";

  // Función para listar grupos/canales desde la conexión
  const handleListGroups = async () => {
    if (!selectedWhatsapp) {
      toast.error("Selecciona una conexión");
      return;
    }

    setLoading(true);

    try {
      // 1. Obtener grupos/canales desde la conexión (WA o Telegram)
      const whatsappResponse = await api.get(`/groups/list/${selectedWhatsapp}`);
      const groups = whatsappResponse.data.groups || [];

      // 2. Obtener grupos desde DB
      const dbResponse = await api.get(`/groups/db/${selectedWhatsapp}`);
      const dbGroups = dbResponse.data.groups || [];

      // ✅ CORRECCIÓN: Normalizar groupId a string para comparación correcta
      const dbGroupIds = dbGroups.map((g) => String(g.groupId || "")).filter((id) => id);

      setGroupsFromWhatsApp(groups);
      setGroupsFromDB(dbGroupIds);
      // ✅ Resetear paginación al cargar nuevos grupos
      setPage(0);

      // 3. Detectar grupos nuevos (que no estan en DB) - comparar como strings
      // Telegram sync ya persiste en DB; la mayoría aparecerán como guardados.
      const newGroupsFiltered = groups.filter((g) => {
        const groupIdStr = String(g.groupId || "");
        return !dbGroupIds.includes(groupIdStr);
      });
      setNewGroups(newGroupsFiltered);

      const channelLabel = whatsappResponse.data.channel === "telegram" ? "grupos/canales de Telegram" : "grupos";
      toast.success(`${groups.length} ${channelLabel} encontrados (${newGroupsFiltered.length} nuevos)`, {
        autoClose: 4000,
      });
    } catch (err) {
      console.error("Error listando grupos:", err);
      toast.error(err.response?.data?.error || "Error al listar grupos", { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar un grupo individual
  const handleSaveGroup = async (group) => {
    if (!selectedWhatsapp) return;

    setSaving(true);

    try {
      // ✅ OPTIMIZACIÓN: Excluir 'participants' del payload
      const { participants, ...groupWithoutParticipants } = group;

      await api.post(`/groups/${selectedWhatsapp}`, groupWithoutParticipants);

      // ✅ CORRECCIÓN: Actualizar contadores correctamente
      // Agregar a set de guardados
      const newSaved = new Set(savedGroups);
      newSaved.add(group.groupId);
      setSavedGroups(newSaved);

      // ✅ Actualizar groupsFromDB para que el contador "Guardados" se actualice
      setGroupsFromDB([...groupsFromDB, group.groupId]);

      // Remover de nuevos grupos
      setNewGroups(newGroups.filter((g) => g.groupId !== group.groupId));

      toast.success(`Grupo "${group.name}" guardado exitosamente`);
    } catch (err) {
      console.error("Error guardando grupo:", err);
      toast.error(err.response?.data?.error || "Error al guardar grupo");
    } finally {
      setSaving(false);
    }
  };

  // Función para guardar todos los grupos nuevos
  const handleSaveAll = async () => {
    if (newGroups.length === 0) {
      toast.info("No hay grupos nuevos para guardar");
      return;
    }

    setSaving(true);

    try {
      // ✅ OPTIMIZACIÓN: Excluir 'participants' del payload para reducir tamaño
      // Los participants se gestionarán en una página dedicada para envío masivo
      const groupsWithoutParticipants = newGroups.map(({ participants, ...rest }) => rest);

      const response = await api.post(`/groups/bulk/${selectedWhatsapp}`, {
        groups: groupsWithoutParticipants,
      });

      // ✅ CORRECCIÓN: Manejar respuesta del backend con errores parciales
      const { saved, errors, groups: savedGroupsData, errorsDetail } = response.data || {};
      const savedCount = saved || savedGroupsData?.length || 0;
      const errorCount = errors || errorsDetail?.length || 0;

      // ✅ Solo actualizar con grupos que realmente se guardaron
      if (savedGroupsData && savedGroupsData.length > 0) {
        const savedGroupIds = savedGroupsData.map((g) => g.groupId);

        // Actualizar set de guardados
        const newSaved = new Set([...savedGroups, ...savedGroupIds]);
        setSavedGroups(newSaved);

        // ✅ Actualizar groupsFromDB para que el contador "Guardados" se actualice
        setGroupsFromDB([...groupsFromDB, ...savedGroupIds]);

        // ✅ Solo remover de newGroups los que realmente se guardaron
        setNewGroups(newGroups.filter((g) => !savedGroupIds.includes(g.groupId)));
      } else if (savedCount > 0) {
        // Si no hay datos detallados pero hay contador, asumir que todos se guardaron
        const allGroupIds = newGroups.map((g) => g.groupId);
        const newSaved = new Set([...savedGroups, ...allGroupIds]);
        setSavedGroups(newSaved);
        setGroupsFromDB([...groupsFromDB, ...allGroupIds]);
        setNewGroups([]);
      }

      // ✅ Mostrar mensaje detallado con éxito y errores
      if (errorCount > 0) {
        toast.warning(`${savedCount} grupos guardados exitosamente, ${errorCount} con errores`);
      } else {
        toast.success(`${savedCount} grupos guardados exitosamente`);
      }
    } catch (err) {
      console.error("Error guardando grupos:", err);
      toast.error(err.response?.data?.error || "Error al guardar grupos");
    } finally {
      setSaving(false);
    }
  };

  const isGroupSaved = (groupId) => savedGroups.has(groupId);

  // ✅ Calcular grupos paginados
  const paginatedGroups = groupsFromWhatsApp.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ✅ Handlers para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ✅ Función para extraer y guardar participantes
  const handleExtractParticipants = async (group) => {
    if (!selectedWhatsapp) {
      toast.error("Selecciona una conexión");
      return;
    }
    if (isTelegramConnection || String(group.groupId || "").startsWith("tg:")) {
      toast.info(
        "En Telegram no se extraen participantes desde aquí. Usa Campañas de Grupos para enviar a grupos/canales donde puedas publicar.",
        { autoClose: 5000 }
      );
      return;
    }

    const groupId = group.groupId;
    setExtractingGroups((prev) => new Set([...prev, groupId]));

    try {
      const response = await api.post(
        `/groups/${selectedWhatsapp}/${groupId}/extract-participants`,
        { groupName: group.name }
      );

      if (response.data.success) {
        // ✅ Sin toast inicial - solo se mostrará el toast verde de "completed" vía Socket.IO
        // El progreso se manejará vía Socket.IO
      } else {
        toast.error(response.data.error || "Error al iniciar extracción");
        setExtractingGroups((prev) => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });
      }
    } catch (err) {
      console.error("Error extrayendo participantes:", err);
      toast.error(err.response?.data?.error || "Error al extraer participantes");
      setExtractingGroups((prev) => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  // ✅ Función para exportar participantes a Excel
  const handleExportParticipants = async (group) => {
    if (!selectedWhatsapp) {
      toast.error("Selecciona una conexión WhatsApp");
      return;
    }

    const groupId = group.groupId;
    setExportingGroups((prev) => new Set([...prev, groupId]));

    try {
      const response = await api.get(
        `/groups/${selectedWhatsapp}/${groupId}/participants/export?source=whatsapp`,
        { responseType: "blob" }
      );

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `participantes-${groupId}-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Excel exportado exitosamente para "${group.name}"`);
    } catch (err) {
      console.error("Error exportando participantes:", err);
      if (err.response?.status === 404) {
        toast.warning("No hay participantes guardados. Extrae primero los participantes.");
      } else if (err.response?.status === 501) {
        toast.warning("Funcionalidad de exportación pendiente de implementación");
      } else {
        toast.error(err.response?.data?.error || "Error al exportar participantes");
      }
    } finally {
      setExportingGroups((prev) => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  // ✅ Listener de Socket.IO para eventos de extracción
  useEffect(() => {
    if (!socketManager) return;

    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);
    if (!socket) return;

    const handleExtractProgress = (data) => {
      const { action, groupId, groupName, total, saved, skipped, message, error } = data;

      if (action === "start") {
        extractionProgressRef.current[groupId] = { total: 0, saved: 0, skipped: 0 };
        // ✅ Sin toast para "start" - solo actualizar estado interno
      } else if (action === "progress") {
        extractionProgressRef.current[groupId] = { total, saved: 0, skipped: 0 };
        // ✅ Sin toast para "progress" - solo actualizar estado interno
      } else if (action === "completed") {
        const progress = extractionProgressRef.current[groupId] || {};
        const finalSaved = saved || progress.saved || 0;
        const finalSkipped = skipped || progress.skipped || 0;
        const finalTotal = total || progress.total || 0;

        toast.success(
          `Extracción completada para "${groupName}": ${finalSaved} guardados, ${finalSkipped} omitidos de ${finalTotal} totales`
        );

        // ✅ Actualizar estado del grupo para mostrar check verde
        setGroupsFromWhatsApp((prev) =>
          prev.map((g) => (g.groupId === groupId ? { ...g, hasParticipantsInDB: true } : g))
        );

        // Limpiar estado de extracción
        setExtractingGroups((prev) => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });

        delete extractionProgressRef.current[groupId];
      } else if (action === "error") {
        toast.error(
          `Error extrayendo participantes de "${groupName}": ${error || message || "Error desconocido"}`
        );

        // Limpiar estado de extracción
        setExtractingGroups((prev) => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });

        delete extractionProgressRef.current[groupId];
      }
    };

    socket.on(`company-${companyId}-extractGroupParticipants`, handleExtractProgress);

    return () => {
      socket.off(`company-${companyId}-extractGroupParticipants`, handleExtractProgress);
    };
  }, [socketManager]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Grupos y canales</Title>
        <MainHeaderButtonsWrapper>
          <GuideOpenButton id="groups" title="Grupos y canales" to="/groups-wa" startId="list" />
          <Button
            variant="contained"
            className={classes.buttonPrimary}
            onClick={handleListGroups}
            disabled={loading || !selectedWhatsapp || saving}
            startIcon={loading ? <CircularProgress size={20} /> : <FileCopyIcon />}
          >
            {loading ? "Listando..." : isTelegramConnection ? "Listar grupos/canales" : "Listar Grupos"}
          </Button>

          {newGroups.length > 0 && (
            <Button
              variant="contained"
              className={classes.buttonSuccess}
              onClick={handleSaveAll}
              disabled={loading || saving || !selectedWhatsapp}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? "Guardando..." : `Guardar Todo (${newGroups.length})`}
            </Button>
          )}
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        {/* ✅ SELECTOR Y ESTADÍSTICAS MEJORADOS */}
        <div className={classes.headerWrapper}>
          <FormControl variant="outlined" className={classes.selectControl}>
            <InputLabel>Conexión</InputLabel>
            <Select
              value={selectedWhatsapp || ""}
              onChange={(e) => setSelectedWhatsapp(e.target.value)}
              label="Conexión"
              disabled={loading || saving}
              renderValue={() => {
                if (!selectedWhatsapp) {
                  return (
                    <div style={{ display: "flex", alignItems: "center", color: "#999" }}>
                      <WhatsAppIcon style={{ marginRight: 8, fontSize: 18 }} />
                      <span>Seleccione una conexión</span>
                    </div>
                  );
                }
                const whatsapp = connectedWhatsApps.find((w) => w.id === Number(selectedWhatsapp));
                if (!whatsapp) {
                  return (
                    <div style={{ display: "flex", alignItems: "center", color: "#999" }}>
                      <WhatsAppIcon style={{ marginRight: 8, fontSize: 18 }} />
                      <span>Seleccione una conexión</span>
                    </div>
                  );
                }
                return (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <WhatsAppIcon
                      style={{
                        marginRight: 8,
                        fontSize: 18,
                        color: String(whatsapp.type || "").toLowerCase() === "telegram" ? "#229ED9" : "#128c7e",
                      }}
                    />
                    <div
                      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                          {whatsapp.name || `Conexión ${whatsapp.id}`}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            backgroundColor:
                              String(whatsapp.type || "").toLowerCase() === "telegram" ? "#229ED9" : "#128c7e",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {String(whatsapp.type || "").toLowerCase() === "telegram" ? "TELEGRAM" : "WHATSAPP"}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            backgroundColor:
                              whatsapp.status === "CONNECTED" ? "#25d366" : "#e1306c",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {whatsapp.status || "DISCONNECTED"}
                        </span>
                      </div>
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {whatsapp.waName
                          ? `${whatsapp.name} - ${whatsapp.waName}`
                          : whatsapp.name
                            ? whatsapp.name
                            : `Conexión ${whatsapp.id}`}
                      </span>
                    </div>
                  </div>
                );
              }}
            >
              {connectedWhatsApps.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <WhatsAppIcon
                      style={{
                        marginRight: 8,
                        fontSize: 18,
                        color: String(whatsapp.type || "").toLowerCase() === "telegram" ? "#229ED9" : "#128c7e",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        flex: 1,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                          {whatsapp.name || `Conexión ${whatsapp.id}`}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            backgroundColor:
                              String(whatsapp.type || "").toLowerCase() === "telegram" ? "#229ED9" : "#128c7e",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {String(whatsapp.type || "").toLowerCase() === "telegram" ? "TG" : "WA"}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            backgroundColor:
                              whatsapp.status === "CONNECTED" ? "#25d366" : "#e1306c",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {whatsapp.status || "DISCONNECTED"}
                        </span>
                      </div>
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {whatsapp.waName
                          ? `${whatsapp.name} - ${whatsapp.waName}`
                          : whatsapp.name
                            ? whatsapp.name
                            : `Conexión ${whatsapp.id}`}
                      </span>
                    </div>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {groupsFromWhatsApp.length > 0 && (
            <>
              <div className={classes.statsCard}>
                <Typography variant="h6">
                  <AssessmentIcon />
                </Typography>
                <Typography variant="body2">
                  <strong>Total:</strong> {groupsFromWhatsApp.length}
                </Typography>
              </div>
              <div className={classes.statsCard}>
                <Typography variant="h6">
                  <CheckCircleIcon />
                </Typography>
                <Typography variant="body2">
                  <strong>Guardados:</strong> {groupsFromDB.length}
                </Typography>
              </div>
              <div className={classes.statsCard}>
                <Typography variant="h6">
                  <FiberNewIcon />
                </Typography>
                <Typography variant="body2">
                  <strong>Nuevos:</strong> {newGroups.length}
                </Typography>
              </div>
            </>
          )}
        </div>

        {/* ✅ TABLA MEJORADA */}
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {paginatedGroups.map((group) => {
              const groupIdStr = String(group.groupId || "");
              const isNew = !groupsFromDB.includes(groupIdStr);
              return (
                <RecordCard
                  key={`group-card-${group.groupId}`}
                  title={group.name}
                  actions={
                    <>
                      {!isTelegramConnection && !String(group.groupId || "").startsWith("tg:") && (
                        <>
                          <IconButton size="small" onClick={() => handleExportParticipants(group)} color="primary">
                            <GetAppIcon />
                          </IconButton>
                          {!group.hasParticipantsInDB && (
                            <IconButton size="small" onClick={() => handleExtractParticipants(group)} color="secondary">
                              <PersonIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                      {isNew && (
                        <IconButton size="small" onClick={() => handleSaveGroup(group)} color="primary">
                          <SaveIcon />
                        </IconButton>
                      )}
                    </>
                  }
                >
                  <RecordCardMeta>{group.groupId}</RecordCardMeta>
                  <RecordCardMeta>
                    {group.participantsCount} participantes · {group.role || "user"}
                    {isNew ? " · Nuevo" : ""}
                  </RecordCardMeta>
                </RecordCard>
              );
            })}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Id Grupo</TableCell>
              <TableCell className={classes.tableHeader}>Nombre Grupo</TableCell>
              <TableCell className={classes.tableHeader}>Participantes</TableCell>
              <TableCell className={classes.tableHeader}>Estado</TableCell>
              <TableCell className={classes.tableHeader}>Conexión</TableCell>
              <TableCell className={classes.tableHeader}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={6} />
            ) : groupsFromWhatsApp.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" className={classes.emptyState}>
                  <GroupIcon className={classes.emptyStateIcon} />
                  <Typography variant="h6" gutterBottom>
                    {selectedWhatsapp
                      ? isTelegramConnection
                        ? "Presiona 'Listar grupos/canales' para comenzar"
                        : "Presiona 'Listar Grupos' para comenzar"
                      : "Selecciona una conexión"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedWhatsapp
                      ? isTelegramConnection
                        ? "Obtén la lista de grupos y canales de la cuenta Telegram conectada (solo donde puedes publicar)"
                        : "Obtén la lista de grupos de la conexión WhatsApp"
                      : "Elige una conexión WhatsApp o Telegram disponible"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedGroups.map((group) => {
                // ✅ CORRECCIÓN: Normalizar groupId a string para comparación
                const groupIdStr = String(group.groupId || "");
                const isNew = !groupsFromDB.includes(groupIdStr);
                const alreadySaved = isGroupSaved(groupIdStr);

                return (
                  <TableRow key={group.groupId} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                        {group.groupId}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <strong>{group.name}</strong>
                      {isNew && (
                        <Chip
                          label="Nuevo"
                          size="small"
                          className={`${classes.statusChip} ${classes.newChip}`}
                          style={{ marginLeft: 8 }}
                        />
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        icon={<GroupIcon />}
                        label={` ${group.participantsCount}`}
                        size="small"
                        className={classes.membersChip}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label={group.role?.toUpperCase() || "USER"}
                        className={`${classes.statusChip} ${
                          group.role === "owner"
                            ? classes.ownerChip
                            : group.role === "superadmin"
                              ? classes.superadminChip
                              : group.role === "admin" || group.isAdmin
                                ? classes.adminChip
                                : classes.userChip
                        }`}
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Activa"
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                        {!isTelegramConnection && !String(group.groupId || "").startsWith("tg:") && (
                          <>
                        {/* Botón Excel - Exportar participantes */}
                        {exportingGroups.has(group.groupId) || loading ? (
                          <Tooltip title="Exportar participantes a Excel">
                            <span>
                              <IconButton
                                size="small"
                                disabled
                                color="primary"
                                className={classes.actionButton}
                              >
                                <CircularProgress size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Exportar participantes a Excel">
                            <IconButton
                              size="small"
                              onClick={() => handleExportParticipants(group)}
                              color="primary"
                              className={classes.actionButton}
                            >
                              <GetAppIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Botón Persona - Extraer y guardar participantes */}
                        {group.hasParticipantsInDB ? (
                          <Tooltip title="Participantes ya guardados en BD">
                            <span>
                              <IconButton size="small" disabled>
                                <CheckCircleIcon style={{ color: "#2e7d32" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : extractingGroups.has(group.groupId) || loading ? (
                          <Tooltip title="Extraer y guardar participantes en BD">
                            <span>
                              <IconButton
                                size="small"
                                disabled
                                color="secondary"
                                className={classes.actionButton}
                              >
                                <CircularProgress size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Extraer y guardar participantes en BD">
                            <IconButton
                              size="small"
                              onClick={() => handleExtractParticipants(group)}
                              color="secondary"
                              className={classes.actionButton}
                            >
                              <PersonIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                          </>
                        )}

                        {/* Botón Guardar grupo (existente) */}
                        {isNew && !alreadySaved ? (
                          saving ? (
                            <Tooltip title="Guardar este grupo">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  color="primary"
                                  className={classes.actionButton}
                                >
                                  <SaveIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Guardar este grupo">
                              <IconButton
                                size="small"
                                onClick={() => handleSaveGroup(group)}
                                color="primary"
                                className={classes.actionButton}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          )
                        ) : (
                          <Tooltip title="Ya guardado en base de datos">
                            <span>
                              <IconButton size="small" disabled>
                                <CheckCircleIcon style={{ color: "#2e7d32" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        )}

        {/* ✅ PAGINACIÓN: Solo aparece cuando hay resultados */}
        {!loading && groupsFromWhatsApp.length > 0 && (
          <TablePagination
            component="div"
            count={groupsFromWhatsApp.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            style={{
              backgroundColor: "transparent",
              color: "inherit",
            }}
          />
        )}
      </Paper>
    </MainContainer>
  );
};

export default GroupsWA;
